
import React, { useRef } from "react";
import ColorButton from "../../common/ColorButton";
import TSTable from "../../common/TSTable";
import { Modal, theme, Tooltip, Popover, Button, Dropdown } from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  UndoOutlined,
  PrinterOutlined,
  TruckOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { PRIMARY_COLOR, PRINT_COLOR } from "../../constants/color";
import { URL_REPORT, URL_TRUCK } from "../../constants/urls";
import { currencyFormat, dateToHumanString, formatComma, toFixed } from "../../frameworks/Util";
import ErrorMessages from "../../common/ErrorMessages";
import PurchasePricePopover from "./PurchasePricePopover";
import PurchaseDRCPopover from "./PurchaseDRCPopover";
import PurchaseWagePopover from "./PurchaseWagePopover";
import { TRUCK_ACTION } from "../../constants/string";
import { POST } from "../../frameworks/HttpClient";
import _ from "lodash";
import ReportButton from "../common/ReportButton";
import dayjs from "dayjs";


export default function PurchaseListView(props) {
  const { state } = props;
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [reportLoading, setReportLoading] = React.useState(false);
  const tableRef = useRef(null);
  const { token : { colorWarning, colorPrimary, colorSuccess }} = theme.useToken();

  const refreshData = async () => {
    if (tableRef.current) {
      await tableRef.current.fetchWithoutResetPage();
    }
  }

  const handleOK = async (id) => {
    setErrorMessages(null)
    try {
      await POST(`${URL_TRUCK.TRUCK_TRANSACTION}${id}/${TRUCK_ACTION.PRICE_CONFIRM}/`)
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const handleUndo = async (id) => {
    setErrorMessages(null)
    try {
      await POST(`${URL_TRUCK.TRUCK_TRANSACTION}${id}/${TRUCK_ACTION.REVERT}/`)
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const confirm = (title, value) => {
    Modal.confirm({
      title: `คำนวนเงินเสร็จสิ้น - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'การคำนวณเงินเสร็จสิ้นแล้วใช่หรือไม่ ?',
      okText: 'ใช่',
      cancelText: 'ไม่ใช่',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : colorPrimary}},
      onOk:async() => handleOK(value),
      onConfirm: () => refreshData()
    }); 
  };

  const confirmUndo = (title, value) => {
    Modal.confirm({
      title: `ย้อนกลับการคำนวณเงิน - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'ต้องการย้อนกลับไปคำนวณราคาใช่หรือไม่ ?',
      okText: 'ใช่',
      cancelText: 'ไม่ใช่',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : colorPrimary}},
      onOk:async() => handleUndo(value),
      onConfirm: () => refreshData()
    }); 
  };

  const columns = [
    {
      title : 'Action',
      key : 'action',
      dataIndex : '',
      render : (value, original) => 
        <center>
          { (state === 'all' && original.price_confirmed_by) && 
          <Tooltip triger='hover' title="พิมพ์ใบตรวจรับ">
            <Dropdown
              trigger='click'
              placement='top'
              arrow={true}
              menu={{items: [
                {
                  key: 'each',
                  label: (
                    <ReportButton
                      url={`${URL_REPORT.RAW_LATEX_IMORT}${original.id}/`}
                      icon={<TruckOutlined/>}
                      block type='text'
                      sentLoading={setReportLoading}
                      sentError={setErrorMessages}>
                      พิมพ์รายบัตร
                    </ReportButton>
                  )
                },
                {
                  key: 'all',
                  label: (
                    <ReportButton
                      url={`${URL_REPORT.RAW_LATEX_IMORT_CUSTOMER}?supplier=${original.supplier}&date=${dayjs(original.created).format('YYYY-MM-DD')}`}
                      icon={<UserOutlined/>}
                      block type='text'
                      sentLoading={setReportLoading}
                      sentError={setErrorMessages}>
                      พิมพ์ทั้งหมด
                    </ReportButton>
                  )
                },
              ]}}
            >
              <ColorButton
                override={PRINT_COLOR}
                style={{ margin: 5 }}
                type='primary'
                icon={<PrinterOutlined />}
                loading={reportLoading}
              />
            </Dropdown>
          </Tooltip>}
          {!original.price_confirmed_by && 
          <Tooltip triger='hover' title="ยืนยันราคา">
            <ColorButton
              type={"primary"}
              override={colorSuccess}
              icon={<CheckCircleOutlined />} 
              onClick={() => confirm(original.vehicle_license_plate, original.id)}
              style={{ margin : 5 }}
              disabled={original.drc ? false : true}
            />
          </Tooltip>
          }
          { (state === 'all' && original.price_confirmed_by) && 
          <Tooltip triger='hover' title="ย้อนกลับ">
            <ColorButton
              type={"primary"}
              override={PRIMARY_COLOR}
              icon={<UndoOutlined />} 
              onClick={() => confirmUndo(original.vehicle_license_plate, original.id)}
              style={{ margin : 5 }}
            />
          </Tooltip>}
        </center>
    },
    {
      title : 'วันที่',
      key : 'created',
      dataIndex : 'created',
      render : (value) => <center>{value ? dateToHumanString(value) : '-'}</center>
    },
    {
      title : 'ลำดับ',
      dataIndex : 'index',
      key : 'index',
      render : (value) => <center>{value}</center>
    },
    {
      title : 'เที่ยว',
      key : 'round_trip',
      dataIndex : 'round_trip',
      width : 50,
      render : (value) => <center>{value}</center>
    },
    {
      title : 'รหัสผู้ขาย',
      key : 'supplier_code',
      dataIndex : 'supplier_code',
      searcher: true,
    },
    {
      title : 'ชื่อผู้ขาย',
      key : 'seller_name',
      dataIndex : 'supplier_name',
      // searcher: true,
    },
    {
      title : 'ทะเบียนรถ',
      key : 'vehicle_license_plate',
      dataIndex : 'vehicle_license_plate',
      searcher: true,
      render : (value) => value ? value : '-'
    },
    {
      title : 'ทะเบียนแทน',
      key : 'substitute_license_plate',
      dataIndex : 'substitute_license_plate',
      ellipsis: true,
      render : (value) => value ? value : '-'
    },
    {
      title : 'น้ำหนัก',
      key : 'net_weight',
      dataIndex : 'net_weight',
      render : (value) => <center>{formatComma(value ? value : 0)}</center>
    },
    {
      title : '%DRC',
      key : 'drc',
      dataIndex : 'drc',
      render: (value, original) => (
        <center>
         {toFixed(value ? value : 0, 2)}
              {(!original.price_confirmed_by) && <PurchaseDRCPopover
                target={original.id}
                onUpdate={() => refreshData()}
              >
                <Tooltip triger='hover' title={"ใส่ DRC"}>
                    <EditOutlined
                      style={{ color: colorWarning, marginLeft: 5 }}
                    />
                </Tooltip>
              </PurchaseDRCPopover>}
        </center>
      ),
    },
    { 
      title : 'น้ำหนักยางแห้ง',
      key : 'dry_weight',
      dataIndex : 'dry_weight',
      render: (value) => <center>{currencyFormat(value ? value : 0)}</center>
    },
    {
      title : 'ราคา',
      key : 'unit_price',
      dataIndex : 'unit_price',
      width : 80,
      render: (value) => <center>{currencyFormat(value ? value : 0)}</center>
    },
    {
      title : 'ปรับราคา',
      key : 'adjust_price',
      dataIndex : 'adjust_price',
      render: (value, original) => (
        <center>
         {toFixed(value ? value : 0, 2)}
              {(!original.price_confirmed_by) &&<PurchasePricePopover
                target={original.id}
                onUpdate={() => refreshData()}
              >
                <Tooltip triger='hover' title={"กรอกราคา"}>
                    <EditOutlined
                      style={{ color: colorWarning, marginLeft: 5 }}
                    />
                </Tooltip>
              </PurchasePricePopover>}
        </center>
      ),
    },
    {
      title : 'ค่าบรรทุก',
      key : 'vehicle_wage',
      dataIndex : 'vehicle_wage',
      align : 'center',
      render: (value, original) => (
        <center>
         {toFixed(value ? value : 0, 2)}
              {(!original.price_confirmed_by) && <PurchaseWagePopover
                target={original.id}
                onUpdate={() => refreshData()}
              >
                <Tooltip triger='hover' title={"กรอกค่าบรรทุก"}>
                    <EditOutlined
                      style={{ color: colorWarning, marginLeft: 5 }}
                    />
                </Tooltip>
              </PurchaseWagePopover>}
        </center>
      ),
    },
    {
      title : 'จำนวนเงินรวม',
      key : 'net_price',
      dataIndex : 'net_price',
      render: (value, original) => <div>
        <Popover
          title='รายละเอียดราคา'
          content={(<div>
            <p>จำนวนเงิน : {currencyFormat(_.get(original, 'net_product_price', 0))}</p>
            <p>ราคาบรรทุก : {currencyFormat(_.get(original, 'total_wage', 0))}</p>
            <p>จำนวนเงินรวม : {currencyFormat(_.get(original, 'net_price', 0))}</p>
          </div>)}
          trigger={['click']}
        >
          <center>{currencyFormat(value ? value : 0)}<Button color='primary' variant='link' icon={<EyeOutlined/>}/></center>
        </Popover>
      </div>
    },
  ]

  return (
   <div>
      <ErrorMessages message={errorMessages}/>
      <TSTable
        ref={tableRef}
        size={'small'}
        columns={columns}
        url={state &&  URL_TRUCK.TRUCK_TRANSACTION}
        params={{ status : state === 'wait' ? 'waiting_confirm' : '', ordering: '-created_date,supplier_code,created'}}
      />
   </div>
  )
}